import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { mdiMapMarker } from '@mdi/js';
import Icon from '@mdi/react';
import { FloorplanDirectionCone } from 'components/FloorplanDirectionCone/FloorplanDirectionCone';
import { getFloorplanDownloadUrl } from 'store/floorplans/services/getFloorplanDownloadUrl.service';

const floorplanUrlCache = new Map();

const useStyles = makeStyles((theme) => ({
  wrapper: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  pinWrapper: {
    position: 'absolute',
    transform: 'translate(-50%, -100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'none',
  },
  label: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginTop: 4,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
}));

export function FloorplanThumbnail({ placement, floorplan }) {
  const classes = useStyles();
  const [src, setSrc] = useState(() =>
    floorplan ? (floorplanUrlCache.get(floorplan.id) ?? null) : null
  );
  const [imgRect, setImgRect] = useState(null);
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!floorplan) return;
    if (floorplanUrlCache.has(floorplan.id)) {
      setSrc(floorplanUrlCache.get(floorplan.id));
      return;
    }
    getFloorplanDownloadUrl(floorplan.project, floorplan.id)
      .then((url) => {
        floorplanUrlCache.set(floorplan.id, url);
        setSrc(url);
      })
      .catch(() => setSrc(null));
  }, [floorplan?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateImgRect = () => {
    const img = imgRef.current;
    const wrapper = wrapperRef.current;
    if (!img || !wrapper || !img.naturalWidth || !img.naturalHeight) return;
    const containerW = wrapper.offsetWidth;
    const containerH = wrapper.offsetHeight;
    const naturalAspect = img.naturalWidth / img.naturalHeight;
    const containerAspect = containerW / containerH;
    let renderedW, renderedH, offsetX, offsetY;
    if (naturalAspect > containerAspect) {
      renderedW = containerW;
      renderedH = containerW / naturalAspect;
      offsetX = 0;
      offsetY = (containerH - renderedH) / 2;
    } else {
      renderedH = containerH;
      renderedW = containerH * naturalAspect;
      offsetX = (containerW - renderedW) / 2;
      offsetY = 0;
    }
    setImgRect({ renderedW, renderedH, offsetX, offsetY });
  };

  if (!floorplan || !placement) return null;

  const pinLeft = imgRect
    ? imgRect.offsetX + placement.x * imgRect.renderedW
    : `${placement.x * 100}%`;
  const pinTop = imgRect
    ? imgRect.offsetY + placement.y * imgRect.renderedH
    : `${placement.y * 100}%`;

  return (
    <div>
      <div className={classes.wrapper} ref={wrapperRef}>
        {src && (
          <img
            ref={imgRef}
            className={classes.image}
            src={src}
            alt={floorplan.name}
            draggable={false}
            onLoad={updateImgRect}
          />
        )}
        <div
          className={classes.pinWrapper}
          style={{ left: pinLeft, top: pinTop }}
        >
          <FloorplanDirectionCone
            direction={placement.direction}
            zoom={1}
            pinTipOffsetPx={2}
          />
          <Icon path={mdiMapMarker} size={1} color="#d32f2f" />
        </div>
      </div>
      <Typography className={classes.label}>{floorplan.name}</Typography>
    </div>
  );
}
